import Vue from 'vue'
import { decode, parsePath, withoutBase, withoutTrailingSlash, normalizeURL } from 'ufo'

import { getMatchedComponentsInstances, getChildrenComponentInstancesUsingFetch, promisify, globalHandleError, urlJoin, sanitizeComponent } from './utils'
import NuxtError from '../layouts/error.vue'
import NuxtLoading from './components/nuxt-loading.vue'

import '../assets/css/tailwind.css'

import '../assets/css/theme-colors.scss'

import '../assets/css/reset.scss'

import '../assets/css/v-tooltip.css'

import '../node_modules/@fortawesome/fontawesome-svg-core/styles.css'

import _7713554d from '../layouts/bates.vue'
import _1a3ae748 from '../layouts/byu.vue'
import _2d222230 from '../layouts/ccga.vue'
import _77b9f938 from '../layouts/centralaz.vue'
import _151117ea from '../layouts/coursedog.vue'
import _6f6c098b from '../layouts/default.vue'
import _2d229ed9 from '../layouts/demo.vue'
import _7974843e from '../layouts/drewcaspersen.vue'
import _f4d2a2bc from '../layouts/drewcla.vue'
import _2d46d1d4 from '../layouts/drewtheo.vue'
import _2d22daa1 from '../layouts/duke.vue'
import _7261fc9c from '../layouts/grmidland.vue'
import _1a3b02a6 from '../layouts/jcu.vue'
import _7c6755bd from '../layouts/lasell.vue'
import _dba4875c from '../layouts/norwich.vue'
import _f98b4ce6 from '../layouts/parker.vue'
import _0981e8ac from '../layouts/richmond.vue'
import _b4271c02 from '../layouts/stanford.vue'
import _8970bbba from '../layouts/trocaire.vue'
import _6be0262b from '../layouts/ugmidland.vue'

const layouts = { "_bates": sanitizeComponent(_7713554d),"_byu": sanitizeComponent(_1a3ae748),"_ccga": sanitizeComponent(_2d222230),"_centralaz": sanitizeComponent(_77b9f938),"_coursedog": sanitizeComponent(_151117ea),"_default": sanitizeComponent(_6f6c098b),"_demo": sanitizeComponent(_2d229ed9),"_drewcaspersen": sanitizeComponent(_7974843e),"_drewcla": sanitizeComponent(_f4d2a2bc),"_drewtheo": sanitizeComponent(_2d46d1d4),"_duke": sanitizeComponent(_2d22daa1),"_grmidland": sanitizeComponent(_7261fc9c),"_jcu": sanitizeComponent(_1a3b02a6),"_lasell": sanitizeComponent(_7c6755bd),"_norwich": sanitizeComponent(_dba4875c),"_parker": sanitizeComponent(_f98b4ce6),"_richmond": sanitizeComponent(_0981e8ac),"_stanford": sanitizeComponent(_b4271c02),"_trocaire": sanitizeComponent(_8970bbba),"_ugmidland": sanitizeComponent(_6be0262b) }

export default {
  render (h, props) {
    const loadingEl = h('NuxtLoading', { ref: 'loading' })

    const layoutEl = h(this.layout || 'nuxt')
    const templateEl = h('div', {
      domProps: {
        id: '__layout'
      },
      key: this.layoutName
    }, [layoutEl])

    const transitionEl = h('transition', {
      props: {
        name: 'layout',
        mode: 'out-in'
      },
      on: {
        beforeEnter (el) {
          // Ensure to trigger scroll event after calling scrollBehavior
          window.$nuxt.$nextTick(() => {
            window.$nuxt.$emit('triggerScroll')
          })
        }
      }
    }, [templateEl])

    return h('div', {
      domProps: {
        id: '__nuxt'
      }
    }, [
      loadingEl,

      transitionEl
    ])
  },

  data: () => ({
    isOnline: true,

    layout: null,
    layoutName: '',

    nbFetching: 0
    }),

  beforeCreate () {
    Vue.util.defineReactive(this, 'nuxt', this.$options.nuxt)
  },
  created () {
    // Add this.$nuxt in child instances
    this.$root.$options.$nuxt = this

    if (process.client) {
      // add to window so we can listen when ready
      window.$nuxt = this

      this.refreshOnlineStatus()
      // Setup the listeners
      window.addEventListener('online', this.refreshOnlineStatus)
      window.addEventListener('offline', this.refreshOnlineStatus)
    }
    // Add $nuxt.error()
    this.error = this.nuxt.error
    // Add $nuxt.context
    this.context = this.$options.context
  },

  async mounted () {
    this.$loading = this.$refs.loading
  },

  watch: {
    'nuxt.err': 'errorChanged'
  },

  computed: {
    isOffline () {
      return !this.isOnline
    },

    isFetching () {
      return this.nbFetching > 0
    },
  },

  methods: {
    refreshOnlineStatus () {
      if (process.client) {
        if (typeof window.navigator.onLine === 'undefined') {
          // If the browser doesn't support connection status reports
          // assume that we are online because most apps' only react
          // when they now that the connection has been interrupted
          this.isOnline = true
        } else {
          this.isOnline = window.navigator.onLine
        }
      }
    },

    async refresh () {
      const pages = getMatchedComponentsInstances(this.$route)

      if (!pages.length) {
        return
      }
      this.$loading.start()

      const promises = pages.map((page) => {
        const p = []

        // Old fetch
        if (page.$options.fetch && page.$options.fetch.length) {
          p.push(promisify(page.$options.fetch, this.context))
        }
        if (page.$fetch) {
          p.push(page.$fetch())
        } else {
          // Get all component instance to call $fetch
          for (const component of getChildrenComponentInstancesUsingFetch(page.$vnode.componentInstance)) {
            p.push(component.$fetch())
          }
        }

        if (page.$options.asyncData) {
          p.push(
            promisify(page.$options.asyncData, this.context)
              .then((newData) => {
                for (const key in newData) {
                  Vue.set(page.$data, key, newData[key])
                }
              })
          )
        }

        return Promise.all(p)
      })
      try {
        await Promise.all(promises)
      } catch (error) {
        this.$loading.fail(error)
        globalHandleError(error)
        this.error(error)
      }
      this.$loading.finish()
    },
    errorChanged () {
      if (this.nuxt.err) {
        if (this.$loading) {
          if (this.$loading.fail) {
            this.$loading.fail(this.nuxt.err)
          }
          if (this.$loading.finish) {
            this.$loading.finish()
          }
        }

        let errorLayout = (NuxtError.options || NuxtError).layout;

        if (typeof errorLayout === 'function') {
          errorLayout = errorLayout(this.context)
        }

        this.setLayout(errorLayout)
      }
    },

    setLayout (layout) {
      if (!layout || !layouts['_' + layout]) {
        layout = 'default'
      }
      this.layoutName = layout
      this.layout = layouts['_' + layout]
      return this.layout
    },
    loadLayout (layout) {
      if (!layout || !layouts['_' + layout]) {
        layout = 'default'
      }
      return Promise.resolve(layouts['_' + layout])
    },
  },

  components: {
    NuxtLoading
  }
}
